import { useEffect, useRef, useState } from 'react'
import type { User } from '../../../domain/user'
import UserForm from '../molecules/UserForm'
import { AxiosUserRepository } from '../../../infrastructure/AxiosUserRepository'
import { useNavigate } from 'react-router-dom'
import { Snackbar } from 'components/atoms/Snackbar/Snackbar'
import { DeleteSection } from 'components/molecules/DeleteSection/DeleteSection'
import AppModal from 'components/atoms/AppModal/AppModal'
import { useUserUpdate } from '../../hooks/useUserUpdate'

interface SnackbarHandle {
    showModal: (value: boolean) => void
}
interface UpdateUserProps {
    userId?: string | number
}

export default function UpdateUser({ userId }: UpdateUserProps) {
    const router = useNavigate()
    const snackbarRef = useRef<SnackbarHandle>(null)
    const [user, setUser] = useState<User | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState<{
        title: string
        description: string
        state: 'success' | 'error'
    }>({
        title: 'Éxito',
        description: 'Acción completada',
        state: 'success',
    })

    const userRepository = new AxiosUserRepository()
    const { updateUser: updateUserHook } = useUserUpdate()

    // Cargar datos del usuario
    useEffect(() => {
        const fetchUser = async () => {
            if (userId === null || userId === undefined) {
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                setError(null)
                const numericId =
                    typeof userId === 'string' ? parseInt(userId, 10) : userId
                const userData = await userRepository.getById(numericId)

                // Mapear datos de la API a la estructura del User
                setUser({
                    id:
                        userData.id !== null &&
                        userData.id !== undefined &&
                        !isNaN(Number(userData.id))
                            ? Number(userData.id)
                            : 1,
                    name: userData.name || '',
                    email: userData.email || '',
                    roleId: userData.roleId || userData.roleId,
                    warehouseId:
                        userData.warehouseId !== null &&
                        userData.warehouseId !== undefined
                            ? Number(userData.warehouseId)
                            : undefined,
                })
            } catch (err) {
                setError('No se pudo cargar los datos del usuario')
                setUser(null)
            } finally {
                setLoading(false)
            }
        }

        fetchUser()
    }, [userId])

    const handleFormSubmit = async (data: {
        formData: {
            fullName: string
            email: string
            warehouse: string
            password: string
            confirm_password: string
        }
        role: string
        template: number
        file: File | null
    }) => {
        try {
            if (userId === null || userId === undefined) return

            const numericId =
                typeof userId === 'string' ? parseInt(userId, 10) : userId

            const updateData: Partial<Omit<User, 'id'>> = {
                name: data.formData.fullName,
                email: data.formData.email,
                roleId: Number(data.role),
                warehouseId:
                    data.formData.warehouse !== null &&
                    data.formData.warehouse !== undefined &&
                    data.formData.warehouse !== ''
                        ? Number(data.formData.warehouse)
                        : undefined,
            }

            const updatedUser = await updateUserHook(numericId, updateData)

            if (updatedUser !== null && updatedUser !== undefined) {
                setSnackbarMessage({
                    title: '¡Éxito!',
                    description: 'Usuario actualizado correctamente',
                    state: 'success',
                })
                snackbarRef.current?.showModal(true)

                router('/users', {
                    state: {
                        title: '¡Éxito!',
                        description: 'Usuario actualizado correctamente',
                        state: 'success',
                    },
                })
            } else {
                setSnackbarMessage({
                    title: 'Error',
                    description: 'Error al actualizar el usuario',
                    state: 'error',
                })
                snackbarRef.current?.showModal(true)
            }
        } catch (err) {
            setSnackbarMessage({
                title: 'Error',
                description: 'Error al actualizar el usuario',
                state: 'error',
            })
            snackbarRef.current?.showModal(true)
        }
    }

    const handleOpenDeleteModal = () => {
        setIsDeleteModalOpen(true)
    }

    const handleConfirmDelete = async () => {
        if (userId === null || userId === undefined) return

        try {
            setDeleting(true)
            const numericId =
                typeof userId === 'string' ? parseInt(userId, 10) : userId
            await userRepository.delete(numericId)

            router('/users', {
                state: {
                    title: '¡Éxito!',
                    description: 'Usuario eliminado correctamente',
                    state: 'success',
                },
            })
        } catch (err) {
            setSnackbarMessage({
                title: 'Error',
                description: 'Error al eliminar el usuario',
                state: 'error',
            })
            snackbarRef.current?.showModal(true)
        } finally {
            setDeleting(false)
        }

        setIsDeleteModalOpen(false)
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-gray-500">Cargando datos del usuario...</p>
            </div>
        )
    }

    return (
        <div>
            <Snackbar
                ref={snackbarRef}
                title={snackbarMessage.title}
                description={snackbarMessage.description}
                state={snackbarMessage.state}
                closeAction
                time={2000}
            />
            <div className="bg-gray-50 py-3">
                <div className="mx-auto">
                    {error !== null && error !== undefined && (
                        <div className="mb-4 rounded-lg bg-red-50 p-4 text-red-700">
                            {error}
                        </div>
                    )}
                    <UserForm
                        onSubmit={handleFormSubmit}
                        mode={'edit'}
                        user={
                            user
                                ? {
                                      id: String(user.id),
                                      name: user.name,
                                      email: user.email,
                                      roleId: user.roleId,
                                      warehouseId:
                                          user.warehouseId !== null &&
                                          user.warehouseId !== undefined
                                              ? String(user.warehouseId)
                                              : undefined,
                                  }
                                : null
                        }
                    />
                </div>
            </div>
            <div className="bg-gray-50 py-3">
                <div className="mx-auto">
                    <DeleteSection
                        title="Eliminar Cuenta"
                        titleButton="Eliminar Cuenta"
                        description="Al confirmar, se eliminará permanentemente la cuenta del usuario. Esto incluye todos los datos y actividades asociadas."
                        disabledButton={deleting}
                        onSubmit={handleOpenDeleteModal}
                    />
                </div>
            </div>

            <AppModal
                title="Eliminar usuario"
                description="¿Estás seguro de que deseas eliminar esta cuenta? Esta acción no se puede deshacer."
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                handleConfirm={handleConfirmDelete}
            />
        </div>
    )
}
